// Footer data for every template (§4.5), lifted from components/LeagueFooter:
// © year + league name, optional footer text, the "Powered by ScoreLM" credit
// (attributionEnabled(league, 'footer')), the section-gated links and the
// Admin link. #623: Venues too, when the league switches that page on (Design ›
// Site sections says an off page leaves menus and footer, so an on one is in both).

import { attributionEnabled, useLeague } from '@/contexts/LeagueContext'
import { sectionEnabled, type SiteSectionKey } from '@/contexts/tenantAccess'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import { useSiteContentAllowed } from '../access/context'
import { useLanguageSwitch, type SiteLanguageSwitch } from './useLanguageSwitch'

export interface SiteFooterLink { key: SiteSectionKey; label: string; href: string }

export interface SiteFooter {
  /** null until the tenant loads. */
  leagueName: string | null
  logoUrl: string | null
  year: number
  footerText: string | null
  /** Gated by site sections; none for a visitor a members-only league's wall stops (QA E1). */
  links: SiteFooterLink[]
  /** attributionEnabled(league, 'footer'); render sf('site.footer.powered-by') when true. */
  poweredBy: boolean
  poweredByHref: string
  adminHref: '/admin'
  /** #1117: the fan language switch; render it (LanguageSelect) when `visible`. */
  language: SiteLanguageSwitch
}

type FooterLinkKey = 'schedule' | 'players' | 'teams' | 'stats' | 'gallery' | 'blog' | 'venues'

/** Labels come from the site-core catalogue (footer.links), so they follow the site's language. */
export const FOOTER_LINKS: readonly { key: FooterLinkKey & SiteSectionKey; href: string }[] = [
  { href: '/schedule', key: 'schedule' },
  { href: '/players', key: 'players' },
  { href: '/teams', key: 'teams' },
  { href: '/stats', key: 'stats' },
  { href: '/gallery', key: 'gallery' },
  { href: '/blog', key: 'blog' },
  { href: '/venues', key: 'venues' },
]

export function useSiteFooter(): SiteFooter {
  const { league } = useLeague()
  const labels = useT(siteCoreMessages).footer.links
  const language = useLanguageSwitch()
  // QA E1: a context read only. Not admitted on a members-only league,
  // every section page shows the wall: no links to it.
  const allowed = useSiteContentAllowed()
  return {
    leagueName: league?.name ?? null,
    logoUrl: league?.logo_url ?? null,
    year: new Date().getFullYear(),
    footerText: league?.branding?.footer_text || null,
    links: league && allowed
      ? FOOTER_LINKS.filter((link) => sectionEnabled(league, link.key)).map((link) => ({ ...link, label: labels[link.key] }))
      : [],
    poweredBy: league ? attributionEnabled(league, 'footer') : false,
    poweredByHref: 'https://scorelm.com',
    adminHref: '/admin',
    language,
  }
}
